/**
 * InstancedJunction.tsx
 *
 * High-performance GPU Instanced Track Junctions (Turntable Rotary Junctions).
 * Faithfully matches cleanroom AMHS rotary junction design from reference image:
 * 1. Stationary dark slate base puck chassis
 * 2. Rotating silver aluminum turntable platform disc
 * 3. Dual-rail track bridge running across the turntable (center diameter only; no radial spoke stubs)
 */

import React, { useMemo, useState, useEffect } from 'react';
import * as THREE from 'three';
import type { LayoutElement } from '../../types';
import {
  BLENDER_TRACK,
  CIRCLE_JUNCTION_BAR_EXTENT_FRAC,
  DUAL_RAIL_LIFT,
  getDualRailProfile,
} from '../../components/canvas/DualRailTrack3D';
import { RAIL_POOL } from '../../utils/threeResourcePool';
import {
  ALL_RAIL_TYPES,
  isJunctionRailType,
  resolveRailType,
  getCircleJunctionRingRadius,
} from '../../utils/topology';
import { getElement3DGroupY } from '../../utils/layout3d';
import { useRailElevation } from '../../components/canvas/railElevationContext';
import { junctionTurntableStore } from '../../store/junctionTurntableStore';
import { getTurntableSettledAngleDeg } from '../../utils/junctionTurntableSim';
import { getSharedStandardMaterial } from '../../utils/threeResourcePool';
import {
  instancedAssetManager,
  InstancedMultiMeshGroup,
  type InstanceTransform,
  type SubMeshPart,
} from './InstancedAssetManager';
import { getAmhsKitTemplateParts } from './amhsKitTemplateStore';
import { useAmhsKitTemplateRevision } from './useAmhsKitTemplateRevision';

const JUNCTION_TEMPLATE_R = 18.0;
const JUNCTION_TEMPLATE_DEPTH = 3.0;
const JUNCTION_TEMPLATE_TRACK_W = 4.0;

// ============================================================================
// TEMPLATE BUILDERS FOR CONTINUOUS CIRCULAR JUNCTION
// ============================================================================

/**
 * Builds the turntable rotary junction template:
 * - Stationary dark slate base puck chassis
 * - Rotating silver aluminum turntable platform disc
 * - Dual-rail track bridge running across the turntable
 */
function buildTurntableJunctionModel(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'TurntableJunctionTemplate';

  const outerR = JUNCTION_TEMPLATE_R;
  const barTrackW = JUNCTION_TEMPLATE_TRACK_W;
  const barDepth = JUNCTION_TEMPLATE_DEPTH;
  const { railH } = getDualRailProfile(barTrackW, barDepth);

  const statorRadius = outerR * 1.10;
  const bearingRadius = outerR * 1.03;
  const rotorRadius = outerR * 0.96;

  const rotorPlateH = Math.max(0.3, barDepth * 0.07);
  const bearingH = Math.max(0.4, barDepth * 0.09);
  const statorBaseH = Math.max(0.45, barDepth * 0.10);

  const trackBottomY = -railH * 0.5;
  const rotorPlateY = trackBottomY - rotorPlateH * 0.5;
  const bearingY = trackBottomY - rotorPlateH - bearingH * 0.5;
  const statorBaseY = trackBottomY - rotorPlateH - bearingH - statorBaseH * 0.5;

  const darkSlateMat = getSharedStandardMaterial({
    color: '#334155',
    metalness: 0.70,
    roughness: 0.38,
    envMapIntensity: 0.80,
  });
  const silverMetalMat = getSharedStandardMaterial({
    color: '#CBD5E1',
    metalness: 0.85,
    roughness: 0.22,
    envMapIntensity: 1.1,
  });
  const slateGrayMat = getSharedStandardMaterial({
    color: '#475569',
    metalness: 0.65,
    roughness: 0.35,
    envMapIntensity: 0.85,
  });
  const railBlackMat = getSharedStandardMaterial({
    color: BLENDER_TRACK.railBlack,
    metalness: 0.82,
    roughness: 0.25,
    envMapIntensity: 0.5,
  });
  const railWhiteMat = getSharedStandardMaterial({
    color: BLENDER_TRACK.railWhite,
    ...RAIL_POOL.aluminum,
  });

  // 1. Stationary Slewing Chassis Housing (Dark Slate Gray Base Puck)
  const basePuck = new THREE.Mesh(
    new THREE.CylinderGeometry(statorRadius * 0.99, statorRadius, statorBaseH, 48),
    darkSlateMat
  );
  basePuck.name = 'Junction_BasePuck';
  basePuck.position.set(0, statorBaseY, 0);
  root.add(basePuck);

  // Slewing Bearing Ring
  const bearingRing = new THREE.Mesh(
    new THREE.CylinderGeometry(bearingRadius, bearingRadius * 1.01, bearingH, 48),
    silverMetalMat
  );
  bearingRing.name = 'Junction_BearingRing';
  bearingRing.position.set(0, bearingY, 0);
  root.add(bearingRing);

  // 2. Rotating Turntable Rotor Disc
  const rotorDisc = new THREE.Mesh(
    new THREE.CylinderGeometry(rotorRadius, rotorRadius * 0.99, rotorPlateH, 48),
    silverMetalMat
  );
  rotorDisc.name = 'Junction_RotorDisc';
  rotorDisc.position.set(0, rotorPlateY, 0);
  root.add(rotorDisc);

  // Outer Bevel Trim
  const outerBevel = new THREE.Mesh(
    new THREE.CylinderGeometry(rotorRadius * 0.98, rotorRadius, rotorPlateH * 0.25, 48),
    slateGrayMat
  );
  outerBevel.name = 'Junction_OuterBevel';
  outerBevel.position.set(0, rotorPlateY + rotorPlateH * 0.3, 0);
  root.add(outerBevel);

  // 4. 4-Layer Dual-Rail Rotating Bridge Track (ring span; live mesh uses per-angle tip sizing)
  const barLen = outerR * 2 * CIRCLE_JUNCTION_BAR_EXTENT_FRAC;
  const l1H = railH * 0.12;
  const layerBelowH = (railH - l1H) / 3;
  const topY = railH * 0.5;
  const l1CenterY = topY - l1H * 0.5;
  const l2CenterY = topY - l1H - layerBelowH * 0.5;
  const l3CenterY = topY - l1H - layerBelowH * 1.5;
  const l4CenterY = topY - l1H - layerBelowH * 2.5;

  const bridgeL1 = new THREE.Mesh(new THREE.BoxGeometry(barLen, l1H, barTrackW), railBlackMat);
  bridgeL1.name = 'Junction_Bridge_Black_1';
  bridgeL1.position.set(0, l1CenterY, 0);

  const bridgeL2 = new THREE.Mesh(new THREE.BoxGeometry(barLen, layerBelowH, barTrackW), railWhiteMat);
  bridgeL2.name = 'Junction_Bridge_White_2';
  bridgeL2.position.set(0, l2CenterY, 0);

  const bridgeL3 = new THREE.Mesh(new THREE.BoxGeometry(barLen, layerBelowH, barTrackW), railBlackMat);
  bridgeL3.name = 'Junction_Bridge_Black_3';
  bridgeL3.position.set(0, l3CenterY, 0);

  const bridgeL4 = new THREE.Mesh(new THREE.BoxGeometry(barLen, layerBelowH, barTrackW), railWhiteMat);
  bridgeL4.name = 'Junction_Bridge_White_4';
  bridgeL4.position.set(0, l4CenterY, 0);

  root.add(bridgeL1, bridgeL2, bridgeL3, bridgeL4);

  return root;
}

function getOrRegisterParts(typeKey: string, builder: () => THREE.Group): SubMeshPart[] {
  let parts = instancedAssetManager.getAssetParts(typeKey);
  if (!parts) {
    const model = builder();
    parts = instancedAssetManager.registerAsset(typeKey, model);
  }
  return parts;
}

export function getOrRegisterJunctionParts(
  _typeKey?: 'JunctionRing' | 'JunctionStationary' | 'JunctionBridge'
): SubMeshPart[] {
  return getAmhsKitTemplateParts('JunctionRing', () =>
    getOrRegisterParts('JunctionRing-v2', buildTurntableJunctionModel)
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export type InstancedJunctionProps = {
  elements: Record<string, LayoutElement>;
  elementIds: readonly string[];
  floorPlanScaleFactor?: number;
  dxfUnitsPerMeter?: number;
  hasFloorCal?: boolean;
  onSelect?: (entityId: string) => void;
};

export const InstancedJunction: React.FC<InstancedJunctionProps> = React.memo(
  ({
    elements,
    elementIds,
    onSelect,
  }) => {
    const railElevation = useRailElevation();
    const [hubRevision, setHubRevision] = useState(0);
    useEffect(() => {
      const unsub = junctionTurntableStore.subscribe(() => setHubRevision((r) => (r + 1) | 0));
      return () => {
        unsub();
      };
    }, []);

    const kitRevision = useAmhsKitTemplateRevision();
    const ringParts = useMemo(() => getOrRegisterJunctionParts('JunctionRing'), [kitRevision]);

    const layoutRails = useMemo(() => Object.values(elements), [elements]);

    const ringInstances = useMemo(() => {
      const rings: InstanceTransform[] = [];

      for (const id of elementIds) {
        const el = elements[id];
        if (!el || !ALL_RAIL_TYPES.has(el.type)) continue;

        const railType = resolveRailType(el);
        if (!isJunctionRailType(railType)) continue;

        const w = el.dimensions.width;
        const h = el.dimensions.height;
        const cx = w * 0.5;
        const cy = h * 0.5;
        const spanR = getCircleJunctionRingRadius(w, h);
        const groupY = getElement3DGroupY(el, undefined, railElevation);
        const baseRotY = (-(el.rotation || 0) * Math.PI) / 180;

        const cos = Math.cos(baseRotY);
        const sin = Math.sin(baseRotY);

        const wx = el.position.x + cx * cos + cy * sin;
        const wy = groupY + DUAL_RAIL_LIFT;
        const wz = el.position.y - cx * sin + cy * cos;

        const scaleR = spanR / JUNCTION_TEMPLATE_R;
        const scaleY = 1.0;
        const settled = getTurntableSettledAngleDeg(el, layoutRails);
        const hubDeg = junctionTurntableStore.getHubAngle(el.id, settled);
        const hubRad = (hubDeg * Math.PI) / 180;

        rings.push({
          id,
          position: [wx, wy, wz],
          rotation: [0, baseRotY + hubRad, 0],
          scale: [scaleR, scaleY, scaleR],
        });
      }

      return rings;
    }, [elements, elementIds, railElevation, hubRevision, layoutRails]);

    if (ringInstances.length === 0) return null;

    return (
      <group name="InstancedJunction">
        <InstancedMultiMeshGroup
          layerId="junction-ring"
          parts={ringParts}
          instances={ringInstances}
          onSelect={onSelect}
        />
      </group>
    );
  }
);

InstancedJunction.displayName = 'InstancedJunction';
export default InstancedJunction;
